/**
 * Entry point for the frontend React micro-frontends.
 * Sets up global styles, i18n, and mounts each app to its root element.
 */

// External packages
import { Suspense, lazy } from "react";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";

// Error tracking
import { initErrorTracking } from "./shared/utils/errorTracking";

// Internal shared modules
import setDocumentDir from "./shared/utils/dom/setDocumentDir";
import mountReactApp from "./shared/utils/dom/mountReactApp";

// Relative imports - Config
import { i18nLoadPath, ROOT_IDS } from "./config/appConfig";

// Styles (always last)
import "./styles/main.css";

// Lazy-loaded app components — Vite splits each into a separate chunk
// so a registration page never downloads selfcare/activation code.
const RegistrationApp = lazy(() => import("./registration/RegistrationApp"));
const ActivationApp = lazy(() => import("./activation/ActivationApp"));
const SelfcareApp = lazy(() => import("./selfcare/SelfcareApp"));
const RepLanding = lazy(() => import("./shared/components/Rep/RepLanding"));

/**
 * Wraps a lazy-loaded component with a Suspense boundary.
 * React.lazy requires Suspense to handle the loading state while
 * the chunk is being fetched.
 */
function withSuspense(LazyComponent) {
  return function SuspenseWrapper() {
    return (
      <Suspense fallback={null}>
        <LazyComponent />
      </Suspense>
    );
  };
}

// Initialize error tracking before React renders
initErrorTracking();

// Get default language from environment or use Hebrew
const defaultLanguage = import.meta.env.VITE_DEFAULT_LANGUAGE || "he";
const forceLanguage = import.meta.env.VITE_FORCE_LANGUAGE;

// i18n initialization.
const buildHash = import.meta.env.VITE_BUILD_HASH || "dev";
i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    load: "languageOnly",
    fallbackLng: defaultLanguage,
    lng: forceLanguage || "he", // Force Hebrew as default
    interpolation: { escapeValue: false },
    backend: { loadPath: `${i18nLoadPath}?v=${buildHash}` },
  })
  .then(() => setDocumentDir(i18n.resolvedLanguage));
i18n.on("languageChanged", setDocumentDir);

// Mount every micro-frontend.
// React.lazy defers chunk loading until the component actually renders,
// and mountReactApp skips apps whose root div doesn't exist on the page.
// Each app gets only the providers it needs (see PROVIDER_PRESETS in mountReactApp).
mountReactApp(ROOT_IDS.REGISTRATION_ROOT, withSuspense(RegistrationApp));
mountReactApp(ROOT_IDS.ACTIVATION_ROOT, withSuspense(ActivationApp), {
  providers: "minimal",
});
mountReactApp(ROOT_IDS.SELFCARE_ROOT, withSuspense(SelfcareApp), {
  providers: "selfcare",
});
mountReactApp(ROOT_IDS.REP_LANDING_ROOT, withSuspense(RepLanding), {
  providers: "minimal",
  skipBootstrap: true,
});
